<template>
  <div class="tab-content-inner">
    <div class="info-alert">
      <CIcon name="cil-info" class="info-icon" />
      <div>
        <strong>Pré-Prompts:</strong> prompts prontos que aparecem como sugestão rápida para os operadores nas telas de pedido, chat e clientes.
        Em vez de digitar do zero, o operador seleciona um pré-prompt e a IA já executa a ação configurada.
        <br />
        <strong>Visibilidade:</strong>
        <em>Público</em> &mdash; visível para os perfis selecionados (ex: todos os vendedores).
        <em>Privado</em> &mdash; visível apenas para quem criou.
      </div>
    </div>

    <crud-base
      ref="crud"
      url="/admin/pre-prompt-ia"
      :columns="columns"
      :onDelete="() => {}"
      :enableSearch="true"
      primaryKey="id"
      :filters="filters"
      :transform="transformItem"
      :useQueryString="false"
      :enableImport="true"
      importTitle="Importar Pré-Prompts em Massa"
      :importTemplate="importTemplate"
    >
      <template v-slot:import-info>
        <div class="import-info-alert">
          <CIcon name="cil-info" class="import-info-icon" />
          <div>
            Envie um arquivo <strong>.csv</strong> seguindo o template.
            Colunas obrigatórias: <code>titulo</code>, <code>conteudo</code>, <code>tipo</code>.
            <br />
            Tipos válidos: pedido, chat, clientes.
            Visibilidade: publico, privado.
            Perfil IDs: separados por vírgula (ex: 1,2,3).
          </div>
        </div>
      </template>

      <template v-slot:filter-search>
        <select
          class="form-control form-control-sm filter-select"
          v-model="filters.tipo"
        >
          <option value="">Todos os tipos</option>
          <option
            v-for="t in tipos"
            :key="t.value"
            :value="t.value"
          >
            {{ t.label }}
          </option>
        </select>
      </template>

      <template v-slot:tipo="{model}">
        <span class="badge-tipo" :class="'tipo-' + model.tipo">
          {{ getLabelTipo(model.tipo) }}
        </span>
      </template>

      <template v-slot:visibilidade="{model}">
        <span
          class="badge-visibilidade"
          :class="'vis-' + model.visibilidade"
        >
          {{ model.visibilidade === 'publico' ? 'Público' : 'Privado' }}
        </span>
      </template>

      <template v-slot:perfis_display="{model}">
        <template v-if="model.visibilidade === 'publico'">
          <span
            v-for="perfil in model.perfis"
            :key="perfil.id"
            class="badge-perfil"
          >
            {{ perfil.descricao }}
          </span>
        </template>
        <template v-else>
          <span class="text-muted">—</span>
        </template>
      </template>

      <template v-slot:action-buttons="{model}">
        <CButton size="sm" color="primary" @click="openEdit(model)" title="Alterar">
          <fa-icon icon="pencil-alt" />
        </CButton>
      </template>

      <template v-slot:header-buttons>
        <CButton color="primary" size="sm" @click="openCreate">
          Novo registro
        </CButton>
      </template>
    </crud-base>

    <!-- Modal Criar/Editar Pré-Prompt -->
    <modal
      name="pre-prompt-form"
      height="auto"
      width="600"
      :scrollable="true"
      :clickToClose="false"
    >
      <CloseModal :close="closeModal" />
      <div class="modalbox">
        <div class="modalbox-content">
          <div class="form-body">
            <div class="form-group">
              <label>Título <span class="text-danger">*</span></label>
              <input
                class="form-control"
                v-model="formData.titulo"
                placeholder="Ex: Reposição mensal, Pedido padrão..."
                :disabled="submitting"
              />
            </div>

            <div class="form-group">
              <label>Conteúdo <span class="text-danger">*</span></label>
              <textarea
                class="form-control"
                v-model="formData.conteudo"
                placeholder="Digite o prompt que será usado como sugestão..."
                rows="6"
                :disabled="submitting"
              />
            </div>

            <div class="row">
              <div class="col-6">
                <div class="form-group">
                  <label>Tipo <span class="text-danger">*</span></label>
                  <select class="form-control" v-model="formData.tipo" :disabled="submitting">
                    <option v-for="t in tipos" :key="t.value" :value="t.value">
                      {{ t.label }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="col-6">
                <div class="form-group">
                  <label>Visibilidade <span class="text-danger">*</span></label>
                  <select class="form-control" v-model="formData.visibilidade" :disabled="submitting">
                    <option value="publico">Público (por perfil)</option>
                    <option value="privado">Privado (só eu)</option>
                  </select>
                </div>
              </div>
            </div>

            <div class="form-group" v-if="formData.visibilidade === 'publico'">
              <label>Perfis</label>
              <div class="perfis-wrapper">
                <div
                  v-for="perfil in perfis"
                  :key="perfil.id"
                  class="perfil-checkbox-item"
                  :class="{ active: isPerfilSelected(perfil.id) }"
                >
                  <label class="perfil-label" @click.prevent="togglePerfil(perfil.id)">
                    <input
                      type="checkbox"
                      :checked="isPerfilSelected(perfil.id)"
                      :disabled="submitting"
                    />
                    <span>{{ perfil.descricao }}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer-bar">
        <CButton variant="outline" color="secondary" @click="closeModal" :disabled="submitting">
          Cancelar
        </CButton>
        <CButton color="primary" @click="handleSubmit" :disabled="submitting || !isFormValid">
          <span v-if="submitting">Salvando...</span>
          <span v-else>{{ isEditing ? 'Salvar Alterações' : 'Criar' }}</span>
        </CButton>
      </div>
    </modal>
  </div>
</template>

<script>
import { get } from "@/helpers/apiRequest";
import { confirmStore, confirmUpdate } from "@/helpers/crud";
import CrudBase from "@/components/crud/CrudBase";
import CloseModal from "@/components/CloseModal";

export default {
  name: "tab-pre-prompts",
  components: {
    CrudBase,
    CloseModal,
  },
  data() {
    return {
      perfis: [],
      filters: {
        tipo: "",
      },
      tipos: [
        { value: "pedido", label: "Pedido" },
        { value: "chat", label: "Chat" },
        { value: "clientes", label: "Clientes" },
      ],
      columns: [
        { label: "Título", field: "titulo", sortable: false },
        { label: "Tipo", field: "tipo", sortable: false, useSlot: true },
        { label: "Visibilidade", field: "visibilidade", sortable: false, useSlot: true },
        { label: "Perfis", field: "perfis_display", sortable: false, useSlot: true },
        { label: "Cadastrado por", field: "autor_nome", sortable: false },
      ],
      importTemplate: {
        header: "titulo;conteudo;tipo;visibilidade;perfil_ids",
        rows: [
          "Reposição mensal;Gere um pedido de reposição mensal com os produtos mais vendidos;pedido;publico;1,2",
          "Consulta estoque;Verifique o estoque disponível dos produtos listados;chat;privado;",
          "Análise de clientes;Analise o perfil de compra dos clientes inativos;clientes;publico;1",
        ],
        filename: "template_pre_prompts_ia.csv",
      },
      formData: {
        titulo: "",
        conteudo: "",
        tipo: "pedido",
        visibilidade: "publico",
        perfil_ids: [],
      },
      editingId: null,
      submitting: false,
    };
  },
  computed: {
    isEditing() {
      return this.editingId !== null;
    },
    isFormValid() {
      return this.formData.titulo && this.formData.conteudo && this.formData.tipo && this.formData.visibilidade;
    },
  },
  methods: {
    getLabelTipo(tipo) {
      const found = this.tipos.find((t) => t.value === tipo);
      return found ? found.label : tipo;
    },
    transformItem(item) {
      return {
        ...item,
        perfil_ids: (item.perfis || []).map((p) => p.id),
      };
    },
    loadPerfis() {
      get("/admin/perfis").then((json) => {
        this.perfis = json.data || json;
      });
    },
    openCreate() {
      this.editingId = null;
      this.formData = {
        titulo: "",
        conteudo: "",
        tipo: "pedido",
        visibilidade: "publico",
        perfil_ids: [],
      };
      this.$modal.show("pre-prompt-form");
    },
    openEdit(item) {
      this.editingId = item.id;
      this.formData = {
        titulo: item.titulo || "",
        conteudo: item.conteudo || "",
        tipo: item.tipo || "pedido",
        visibilidade: item.visibilidade || "publico",
        perfil_ids: (item.perfis || []).map((p) => p.id),
      };
      this.$modal.show("pre-prompt-form");
    },
    closeModal() {
      this.$modal.hide("pre-prompt-form");
    },
    isPerfilSelected(perfilId) {
      return this.formData.perfil_ids.includes(perfilId);
    },
    togglePerfil(perfilId) {
      if (this.submitting) return;
      const index = this.formData.perfil_ids.indexOf(perfilId);
      if (index >= 0) {
        this.formData.perfil_ids.splice(index, 1);
      } else {
        this.formData.perfil_ids.push(perfilId);
      }
    },
    handleSubmit() {
      if (!this.isFormValid || this.submitting) return;
      this.submitting = true;

      const payload = { ...this.formData };
      const onSuccess = () => {
        this.submitting = false;
        this.closeModal();
        this.$refs.crud.loadData();
      };
      const onError = () => {
        this.submitting = false;
      };

      if (this.isEditing) {
        confirmUpdate(`/admin/pre-prompt-ia/${this.editingId}`, payload, onSuccess, onError);
      } else {
        confirmStore("/admin/pre-prompt-ia", payload, onSuccess, onError);
      }
    },
  },
  mounted() {
    this.loadPerfis();
  },
};
</script>

<style scoped>
.tab-content-inner {
  padding: 20px;
}

.info-alert {
  display: flex;
  gap: 10px;
  padding: 12px 16px;
  background: #edf8f9;
  border: 1px solid #c5e6e9;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 13px;
  color: #344054;
  line-height: 1.5;
}

.info-icon {
  flex-shrink: 0;
  margin-top: 2px;
  color: #2298a3;
}

.filter-select {
  width: auto;
  min-width: 180px;
}

.badge-perfil {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: #f2f2f8;
  color: #3a3e81;
  margin-right: 4px;
  margin-bottom: 2px;
}

.badge-tipo {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.tipo-pedido {
  background: #edf8f9;
  color: #2298a3;
}

.tipo-chat {
  background: #f2f2f8;
  color: #3a3e81;
}

.tipo-clientes {
  background: #fff3e0;
  color: #e65100;
}

.badge-visibilidade {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.vis-publico {
  background: #e8f5e9;
  color: #2e7d32;
}

.vis-privado {
  background: #fce4ec;
  color: #c62828;
}

.import-info-alert {
  display: flex;
  gap: 10px;
  padding: 12px 16px;
  background: #edf8f9;
  border: 1px solid #c5e6e9;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #344054;
  line-height: 1.5;
}

.import-info-alert code {
  background: #fff;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 12px;
}

.import-info-icon {
  flex-shrink: 0;
  margin-top: 2px;
  color: #2298a3;
}

/* ===== FORM MODAL ===== */
.form-body {
  padding: 16px 20px;
}

.form-body .form-group {
  margin-bottom: 10px;
}

.form-body .form-group > label {
  font-size: 12px;
  font-weight: 500;
  color: #344054;
  margin-bottom: 3px;
}

/* ===== PERFIS MULTI-CHECKBOX (padrão Tipos de Colaborador) ===== */
.perfis-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.perfil-checkbox-item {
  display: flex;
  flex-direction: column;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  transition: all 0.15s;
  background: #fff;
}

.perfil-checkbox-item:hover {
  border-color: #b0bec5;
}

.perfil-checkbox-item.active {
  border-color: #3a3e81;
  background: #f2f2f8;
}

.perfil-label {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 13px;
  color: #344054;
  font-weight: 500;
  margin: 0;
}

.perfil-label input[type="checkbox"] {
  accent-color: #3a3e81;
  pointer-events: none;
}

/* ===== MODAL FOOTER ===== */
.modal-footer-bar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid #e9ecef;
  background: #fff;
}
</style>
